import { Injectable, signal } from '@angular/core';
import { Observable, Subject, of, take } from 'rxjs';

/**
 * Signing in again over a screen instead of leaving it, when the session ended while someone was writing.
 *
 * A screen with work that must not be lost (the update editor) guards itself: `guard()` on opening, `release()` on
 * leaving. While a screen guards, the error interceptor answers a 401 not by logging out and going to /inloggen but by
 * asking here for a new session: the dialog (SessionExpiredDialogComponent, in the admin shell) opens once, however many
 * requests were refused at the same moment, and every one of them gets the same answer. Everywhere else a 401 ends the
 * session as before.
 *
 * "Niet nu" is an answer until the next change by the user (allowPromptAgain()): requests refused after it, such as
 * the automatic save of text typed before, fail without asking again, so the dialog does not come straight back.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiredService {
  private guards = 0;
  private answer: Subject<boolean> | null = null;
  private declined = false;
  private readonly promptingSignal = signal(false);

  /** Whether the dialog is asking to sign in again. */
  readonly prompting = this.promptingSignal.asReadonly();

  guard(): void {
    this.guards += 1;
    this.declined = false;
  }

  release(): void {
    this.guards = Math.max(0, this.guards - 1);
    this.declined = false;
  }

  /** The user changed something after "Niet nu": a refused request may ask again. */
  allowPromptAgain(): void {
    this.declined = false;
  }

  isGuarding(): boolean {
    return this.guards > 0;
  }

  /** Emits once: true when the user signed in again, false when the dialog was closed without. */
  askToSignInAgain(): Observable<boolean> {
    if (this.declined) {
      return of(false);
    }

    if (this.answer === null) {
      this.answer = new Subject<boolean>();
      this.promptingSignal.set(true);
    }

    return this.answer.pipe(take(1));
  }

  /** Called by the dialog: after a successful login, or when it is closed. */
  finish(signedIn: boolean): void {
    const answer = this.answer;
    this.answer = null;
    this.declined = !signedIn;
    this.promptingSignal.set(false);
    answer?.next(signedIn);
    answer?.complete();
  }
}
